import { ReactNode } from 'react';
import { Alert, Button, TextBox } from '@superdispatch/ui-lab';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
} from '@material-ui/core';
import {
  ColorDynamic,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import styled from 'styled-components';
import { Form, FormikProvider, useField } from 'formik';
import { FormikTextField, useFormikEnhanced } from '@superdispatch/forms';
import { trackEvent } from 'shared/helpers/Analytics';
import { LoadSuggestionDTO } from './data/LoadSuggestionDTO';
import { formatAddressLine } from 'shared/helpers/AddressHelpers';
import {
  CarrierSettings,
  useCarrierSettings,
} from 'shared/settings/CarrierSettingsAPI';
import { joinStrings } from 'shared/utils/StringUtils';
import {
  formatPaymentMethod,
  formatPaymentTerm,
  formatVehicleType,
} from '@superdispatch/sdk';

const FORM_ERROR_KEY = 'form_error';

interface FormValues {
  comment: string;
  reasons: string[];
  other_comment: string;
  form_error?: string;
}

const StyledChip = styled(Chip)`
  padding: 6px;
  font-weight: 600;
  color: ${ColorDynamic.Dark300};

  &.MuiChip-outlinedPrimary {
    color: ${ColorDynamic.Blue500};
  }
`;

function submitFeedback(
  load: LoadSuggestionDTO,
  values: FormValues,
  carrier?: CarrierSettings,
) {
  return new Promise((resolve) => {
    trackEvent(
      'Carrier Clicked Report Suggestion',
      {
        user_guid: carrier?.user.guid,
        carrier_guid: carrier?.carrier.guid,
        carrier_name: carrier?.carrier.name,
        utm_medium: 'Suggested Loads Drawer',
        order_guid: load.guid,
        order_number: load.number,
        created_at: load.created_at,
        price: load.price,
        payment: joinStrings(
          ', ',
          load.price,
          formatPaymentMethod(load.payment.method),
          formatPaymentTerm(load.payment.terms),
        ),
        payment_method: load.payment.method,
        payment_terms: load.payment.terms,
        pickup: formatAddressLine(
          load.pickup.venue.zip,
          load.pickup.venue.city,
          load.pickup.venue.state,
        ),
        delivery: formatAddressLine(
          load.delivery.venue.zip,
          load.delivery.venue.city,
          load.delivery.venue.state,
        ),
        drivers: load.driver_names?.join(', '),
        vehicle_count: load.vehicles.length,
        transport_type: load.transport_type,
        shipper_name: load.shipper.name,
        comment: values.comment,
        other_comment: values.other_comment,
        reason: values.reasons.join('; '),
        based_on_algorithm: load.algorithms.join(', '),
        vehicles: load.vehicles
          .map(({ year, make, model, type, curb_weight }) =>
            joinStrings(
              ', ',
              joinStrings(' ', year, make, model, formatVehicleType(type)),
              curb_weight && `Curb weight: ${curb_weight}lb`,
            ),
          )
          .join(', '),
      },
      () => {
        resolve(true);
      },
    );
  });
}

const FeedbackCategory = ({
  title,
  options,
}: {
  title: ReactNode;
  options: string[];
}) => {
  const [{ value }, _meta, { setValue }] = useField<string[]>('reasons');

  return (
    <Stack>
      <TextBox variant="heading-5">{title}</TextBox>

      <Inline>
        {options.map((option) => (
          <StyledChip
            key={option}
            label={option}
            variant="outlined"
            clickable={true}
            color={value.includes(option) ? 'primary' : 'default'}
            onClick={() => {
              if (value.includes(option)) {
                void setValue(value.filter((item) => item !== option));
              } else {
                void setValue([...value, option]);
              }
            }}
          />
        ))}
      </Inline>
    </Stack>
  );
};

interface LoadSuggestionsFeedbackDialogContentProps {
  load: LoadSuggestionDTO;
  onClose: () => void;
}

function LoadSuggestionsFeedbackDialogContent({
  load,
  onClose,
}: LoadSuggestionsFeedbackDialogContentProps) {
  const { addSnackbar } = useSnackbarStack();
  const { data: carrierSettings } = useCarrierSettings();

  const formik = useFormikEnhanced<FormValues, unknown>({
    initialValues: {
      comment: '',
      reasons: [],
      other_comment: '',
      form_error: undefined,
    },
    validate: (values) => {
      if (!values.comment && !values.reasons.length) {
        return {
          [FORM_ERROR_KEY]:
            'Please select at least one reason or add a comment.',
        };
      }

      if (values.reasons.includes('Other') && !values.other_comment) {
        return { other: 'Please enter a reason for selecting “Other”.' };
      }

      return undefined;
    },
    onSubmit: (values) => {
      return submitFeedback(load, values, carrierSettings);
    },
    onSubmitSuccess() {
      addSnackbar('Thank you! Your feedback has been submitted.');
      onClose();
    },
  });

  return (
    <FormikProvider value={formik}>
      <Form>
        <DialogTitle>Tell Us Why This Load Doesn&apos;t Work</DialogTitle>
        <DialogContent>
          <Stack space="xxsmall">
            <TextBox>
              Your feedback helps us suggest better loads for you.
            </TextBox>

            {formik.touched[FORM_ERROR_KEY] &&
              formik.errors[FORM_ERROR_KEY] && (
                <Alert severity="critical">
                  {formik.errors[FORM_ERROR_KEY]}
                </Alert>
              )}
          </Stack>

          <FormikTextField
            rows={2}
            name="comment"
            fullWidth={true}
            margin="normal"
            multiline={true}
            placeholder="What's wrong with this load suggestion?"
          />

          <Stack space="small">
            <FeedbackCategory
              title="Location and Route"
              options={[
                "My driver doesn't go here",
                'Outside our preferred lanes',
                'Location is hard to access',
              ]}
            />

            <FeedbackCategory
              title="Payment"
              options={['Price is too low', "Payment terms don't work for us"]}
            />

            <FeedbackCategory
              title="Load and Equipment"
              options={[
                'Oversized or overweight',
                'Vehicle is inoperable or damaged',
                'Requires enclosed trailer — we only have open',
              ]}
            />

            <FeedbackCategory
              title="Misc"
              options={["We don't work with this shipper", 'Other']}
            />

            {formik.values.reasons.includes('Other') && (
              <FormikTextField
                name="other_comment"
                fullWidth={true}
                multiline={true}
                rows={2}
              />
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Report
          </Button>
        </DialogActions>
      </Form>
    </FormikProvider>
  );
}

interface LoadSuggestionsFeedbackDialogProps {
  load: LoadSuggestionDTO;
  open: boolean;
  onClose: () => void;
}

export function LoadSuggestionsFeedbackDialog({
  open,
  load,
  onClose,
}: LoadSuggestionsFeedbackDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <LoadSuggestionsFeedbackDialogContent load={load} onClose={onClose} />
    </Dialog>
  );
}
